import React, { useState, useEffect, useMemo, useCallback, useRef, startTransition } from "react";
import { AccountFinal } from "@/api/entities";
import { MomentumScore } from "@/api/entities";
import { Deal } from "@/api/entities";
import { PersonFinal } from "@/api/entities";
import { parseISO, subWeeks, isWithinInterval } from 'date-fns';
import { Link } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { BadgeCheck, Building2, Search, Filter, ChevronRight, ArrowUp, ArrowDown } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useToast } from "@/components/ui/use-toast";
import { Department } from "@/api/entities";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { applyTenantOverrides } from '@/components/utils/tenantOverrides';
import {
  translateIncumbentSentiment,
  translateProcurementFriendliness,
} from '@/lib/accountClassificationLabels';
import AccountPlaybookMarker from '../components/AccountPlaybookMarker';
import ListedForSaleBadge, { isListedForSale } from '../components/accounts/ListedForSaleBadge';
import AccountFitBadge from '../components/accounts/AccountFitBadge';
import AccountTierSelect from '../components/accounts/AccountTierSelect';
import { tierSortNumber } from '@/lib/accountTier';
import { AccountPlaybookStatusProvider } from '../components/AccountPlaybookStatusContext';
import { useTranslation } from '@/components/utils/translations';
import { resolveEffectiveTenant, isEvasysFamilyTenant, isFeedbackfruitsFamilyTenant, isDemarqueFamilyTenant, isBibliothecaFamilyTenant, tenantIdsEqual } from '@/lib/tenantUtils';
import { useUser, useCurrentTenant } from '@/contexts/UserContext';
import { buildTenantFilter } from '@/components/utils/accountResolver';
import { isUntrustedAccountLogoUrl, accountNameInitials, interpretRemoveAccountResult } from '@/lib/marketExperience';
import { canShowRemoveAccountAction, isMarketModuleTenantId, removeMarketAccount, clearAccountPageCache } from '@/lib/marketExperienceApi';
import { usePilotStatus } from '@/hooks/usePilotStatus';
import { clearHomeDestinationCache } from '@/hooks/useHomeDestination';
import AccountOverflowMenu from '../components/accounts/AccountOverflowMenu';
import RemoveAccountDialog from '../components/accounts/RemoveAccountDialog';
import { notify } from '@/components/ui/notify';
import {
  EVASYS_TARGET_PRODUCT_OPTIONS,
  LEARNWISE_TARGET_PRODUCT_OPTIONS,
  CAYUSE_TARGET_PRODUCT_OPTIONS,
  deriveEvasysTargetProductValue,
  deriveLearnwiseTargetProductValue,
  deriveCayuseTargetProductValue,
  isSysExamProductContextTenant,
  BLACKBOARD_TARGET_PRODUCT_OPTIONS,
  deriveBlackboardTargetProductValue,
  isBlackboardProductContextTenant,
  UNIT4_TARGET_PRODUCT_OPTIONS,
  deriveUnit4TargetProductValue,
  isUnit4ProductContextTenant,
  CONSTRUCTOR_TARGET_PRODUCT_OPTIONS,
  deriveConstructorTargetProductValue,
  isConstructorProductContextTenant,
  ACCESS_PEOPLE_TARGET_PRODUCT_OPTIONS,
  deriveAccessPeopleTargetProductValue,
  isAccessPeopleProductContextTenant,
  FOURSCIENCE_TARGET_PRODUCT_OPTIONS,
  deriveFourScienceTargetProductValue,
  isFourScienceProductContextTenant,
  TESTWE_TARGET_PRODUCT_OPTIONS,
  deriveTestweTargetProductValue,
  isTestweProductContextTenant,
} from '@/lib/accountTargetProducts';
import { datasetHasSubscribers, formatAccountCount } from '@/lib/accountSizeFields';

const ACCOUNTS_CACHE_KEY = 'accounts-cache-v3';
const CACHE_DURATION = 10 * 60 * 1000; // 10 minutes

// --- New Rate Limit & Tenant Constants/Helpers ---
const DEFAULT_TENANT = 'instructure'; // Default tenant if user/currentTenant is not set
const RATE_LIMIT_COOLDOWN_DURATION = 60 * 1000; // 1 minute cooldown
let rateLimitCooldownUntil = 0;
let rateLimitToastShownThisCooldown = false;

const isInRateLimitCooldown = () => Date.now() < rateLimitCooldownUntil;
const getRemainingCooldownTime = () => Math.max(0, rateLimitCooldownUntil - Date.now());
const formatRemainingTime = (ms) => {
  const seconds = Math.ceil(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.ceil(seconds / 60);
  return `${minutes}m`;
};
const shouldShowRateLimitToast = () => !rateLimitToastShownThisCooldown;
const markRateLimitToastShown = () => { rateLimitToastShownThisCooldown = true; };
const setRateLimitCooldown = () => {
  rateLimitCooldownUntil = Date.now() + RATE_LIMIT_COOLDOWN_DURATION;
  rateLimitToastShownThisCooldown = false;
};
const clearRateLimitCooldown = () => {
  rateLimitCooldownUntil = 0;
  rateLimitToastShownThisCooldown = false;
};

// Helper function to retry with exponential backoff (updated to use the new rate limit helpers)
const fetchWithRetry = async (fn, maxRetries = 3, baseDelay = 2000) => {
  for (let i = 0; i < maxRetries; i++) {
    try {
      return await fn();
    } catch (error) {
      const isRateLimit = error.response?.status === 429 || (error.message && error.message.includes('Rate limit'));
      if (isRateLimit && i < maxRetries - 1) {
        setRateLimitCooldown(); // Set cooldown if rate limit hit
        const delay = baseDelay * Math.pow(2, i);
        console.warn(`Rate limit hit, retrying in ${delay/1000}s... (attempt ${i + 1}/${maxRetries})`);
        await new Promise(resolve => setTimeout(resolve, delay));
      } else {
        throw error;
      }
    }
  }
};
// --- End New Rate Limit & Tenant Constants/Helpers ---


// Helper to sanitize momentum score change values (handles en-dash issue)
const sanitizeMomentumChange = (value) => {
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    // Replace en-dash (–) with regular minus sign (-)
    const cleaned = value.replace(/–/g, '-').trim();
    const parsed = parseFloat(cleaned);
    return isNaN(parsed) ? 0 : parsed;
  }
  return 0;
};

const MOMENTUM_WINDOW_WEEKS = 4;

/** Same sources as AccountFinal / alerts: momentum rows may only set date_published. */
function parseMomentumPeriodDate(raw) {
  if (raw == null || raw === '') return null;
  if (raw instanceof Date && !isNaN(raw.getTime())) return raw;
  const s = String(raw).trim();
  if (!s) return null;
  const iso = parseISO(s);
  if (!isNaN(iso.getTime())) return iso;
  const fallback = new Date(s);
  if (!isNaN(fallback.getTime())) return fallback;
  return null;
}

function momentumRowMatchesAccount(m, acc) {
  const mid = m?.account_id != null && m.account_id !== '' ? String(m.account_id).trim() : '';
  const accBusinessId = acc?.account_id != null && acc.account_id !== '' ? String(acc.account_id).trim() : '';
  const accPk = acc?.id != null && acc.id !== '' ? String(acc.id).trim() : '';
  if (mid && (mid === accBusinessId || mid === accPk)) return true;

  const inst = (m?.institution_name || '').trim().toLowerCase();
  const name = (acc?.account_name || '').trim().toLowerCase();
  return Boolean(inst && name && inst === name);
}

/**
 * Rolling sum of momentum_score_change for rows in [now - weeks, now] (inclusive).
 * Rows without a parsable period on date_published/weekly_period are skipped.
 */
function attachMomentumRollingSum(accounts, momentumRows, weeks = MOMENTUM_WINDOW_WEEKS) {
  const now = new Date();
  const windowStart = subWeeks(now, weeks);
  const sanitizedRows = (momentumRows || []).map((m) => ({
    ...m,
    momentum_score_change: sanitizeMomentumChange(m.momentum_score_change),
  }));

  return (accounts || []).map((acc) => {
    const accountMomentum = sanitizedRows.filter((m) => {
      if (!momentumRowMatchesAccount(m, acc)) return false;
      const periodDate = parseMomentumPeriodDate(m.date_published || m.weekly_period);
      if (!periodDate) return false;
      return isWithinInterval(periodDate, { start: windowStart, end: now });
    });
    const momentum_4w = accountMomentum.reduce((sum, row) => sum + (row.momentum_score_change || 0), 0);
    return { ...acc, momentum_4w };
  });
}

function readAccountsCache(tenantId) {
  try {
    // Clear old cache format (without tenant_id in key) on first read
    const oldKey = ACCOUNTS_CACHE_KEY;
    if (sessionStorage.getItem(oldKey)) {
      console.log('🧹 Accounts: Clearing old cache format (without tenant_id)');
      sessionStorage.removeItem(oldKey);
    }
    
    const key = `${ACCOUNTS_CACHE_KEY}-${tenantId}`;
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const data = JSON.parse(raw);
    
    // Reject cache data without tenantId field (old format)
    if (!data.tenantId) {
      console.warn('⚠️ Accounts: Cache missing tenantId field, clearing old cache');
      sessionStorage.removeItem(key);
      return null;
    }
    
    // Verify tenant matches (safety check)
    if (data.tenantId !== tenantId) {
      console.warn('⚠️ Accounts: Cache tenant mismatch, clearing:', { cached: data.tenantId, current: tenantId });
      sessionStorage.removeItem(key);
      return null;
    }
    if (Date.now() - data.cachedAt > CACHE_DURATION) {
      sessionStorage.removeItem(key);
      return null;
    }
    return data;
  } catch { return null; }
}

function writeAccountsCache(tenantId, snapshot) {
  try {
    const key = `${ACCOUNTS_CACHE_KEY}-${tenantId}`;
    sessionStorage.setItem(
      key,
      JSON.stringify({ ...snapshot, tenantId, cachedAt: Date.now() })
    );
  } catch {}
}

const getBandFromDate = (date) => {
  if (!date) return 'N/A';
  const now = new Date();
  const decisionDate = new Date(date);
  
  if (isNaN(decisionDate.getTime()) || decisionDate < now) return 'N/A';
  
  const diffInMonths = (decisionDate.getFullYear() - now.getFullYear()) * 12 + (decisionDate.getMonth() - now.getMonth());

  if (diffInMonths < 12) return '<12m';
  if (diffInMonths >= 12 && diffInMonths < 24) return '12-24m';
  if (diffInMonths >= 24) return '24+m';
  return 'N/A';
};

const formatDecisionWindow = (band) => {
  const formatMap = {
    '<12m': '<12 months',
    '12-24m': '12-24 months', 
    '24+m': '>24 months',
    'N/A': 'N/A'
  };
  return formatMap[band] || band;
};

const getAccountDecisionWindowDisplay = (row) => {
  if (!row) return 'N/A';
  return row.decision_window_band
    ? formatDecisionWindow(row.decision_window_band)
    : formatDecisionWindow(getBandFromDate(row.decision_window_date));
};

const accountHasDecisionWindowValue = (row) => {
  const display = getAccountDecisionWindowDisplay(row);
  return Boolean(display && display !== 'N/A');
};

const getRenewalRiskColor = (risk) => {
  switch (risk) {
    case 'Low': return 'bg-green-100 text-green-800';
    case 'Medium': return 'bg-yellow-100 text-yellow-800';
    case 'High': return 'bg-red-100 text-red-800';
    default: return 'bg-gray-100 text-gray-800';
  }
};

function ExistingCustomerIcon() {
  return (
    <TooltipProvider delayDuration={0}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            tabIndex={0}
            aria-label="Existing customer"
            className="inline-flex shrink-0 text-[#00C2C2] cursor-help"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            <BadgeCheck className="w-3.5 h-3.5" aria-hidden="true" />
          </span>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs bg-[#0A1A2F] text-white border-0 shadow-lg">
          <p className="text-xs leading-snug">
            <span className="font-semibold">Existing customer.</span>{' '}
            This institution is on your client list or is running your product according to its recorded vendor stack.
          </p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/**
 * Normalizes department data, ensuring that for each unique account-department pair,
 * only the entry with the highest department_fit_score is retained.
 * This helps in handling potential duplicate or redundant department entries.
 * @param {Array<Department>} departments - The raw department array.
 * @param {string} accountIdField - The field in the department object that links to an account (e.g., 'account_id').
 * @returns {Array<Department>} An array of normalized department objects.
 */
const normalizeDepartments = (departments, accountIdField) => {
  const seenDepartments = new Map(); // Key: `${accountId}-${departmentName}`, Value: best department object

  departments.forEach(dept => {
    const accountId = dept[accountIdField];
    if (!accountId || !dept.department) return;

    const key = `${accountId}-${dept.department}`;
    const currentScore = Number(dept.department_fit_score) || 0;

    const existing = seenDepartments.get(key);
    if (!existing || currentScore > (Number(existing.department_fit_score) || 0)) {
      seenDepartments.set(key, dept);
    }
  });

  return Array.from(seenDepartments.values());
};

export default function Accounts({ currentTenant: currentTenantProp }) {
  const { t, language } = useTranslation();
  const contextUser = useUser();
  const contextTenant = useCurrentTenant();
  const currentTenant = currentTenantProp || contextTenant;
  const effectiveTenant = useMemo(() => resolveEffectiveTenant(
    contextUser?.role === 'admin' ? localStorage.getItem('admin-active-tenant') : null,
    currentTenant,
    contextUser,
    DEFAULT_TENANT
  ), [currentTenant, contextUser]);
  const [allAccounts, setAllAccounts] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [momentumScores, setMomentumScores] = useState([]);
  const [deals, setDeals] = useState([]);
  const [people, setPeople] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hydratedFromCache, setHydratedFromCache] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [countryFilter, setCountryFilter] = useState("all");
  const [departmentFilter, setDepartmentFilter] = useState("all");
  const [sortConfig, setSortConfig] = useState({ key: 'account_fit_score', direction: 'descending' });
  
  const aliveRef = useRef(true);
  const loadAttemptedRef = useRef(false);
  const dataLoadedRef = useRef(false);
  const lastLoadedTenantRef = useRef(null);

  const { toast } = useToast();

  const isFaria = effectiveTenant === 'faria';
  const isCalmedica = effectiveTenant === 'calmedica';
  const isSkillwell = effectiveTenant === 'skillwell';
  const isWayground = effectiveTenant === 'wayground';
  const isPlataformaa = effectiveTenant === 'plataformaa';
  const isSysExamTenant = isSysExamProductContextTenant(effectiveTenant);
  const isEdusign = effectiveTenant === 'edusign';
  const isCayuse = effectiveTenant === 'cayuse';
  const isLearnwise = effectiveTenant === 'learnwise';
  const isEvasys = isEvasysFamilyTenant(effectiveTenant);
  const isRumi = effectiveTenant === 'rumi';
  const isFeedbackfruits = isFeedbackfruitsFamilyTenant(effectiveTenant);
  const isScantron = effectiveTenant === 'scantron';
  const isInfonetica = effectiveTenant === 'infonetica';
  const isDemarque = isDemarqueFamilyTenant(effectiveTenant);
  const showAccountTierColumn =
    isEvasys || isRumi || isFeedbackfruits || isScantron || isInfonetica || isDemarque
    || isBibliothecaFamilyTenant(effectiveTenant)
    || tenantIdsEqual(effectiveTenant, 'fullfabric_V2')
    || tenantIdsEqual(effectiveTenant, 'surpass_V2')
    // [2026-10-18] testwe carries surpass_V2 + feedbackfruits_V2's footprint; both show the tier.
    || tenantIdsEqual(effectiveTenant, 'testwe');
  const showDecisionWindowForTenant =
    tenantIdsEqual(effectiveTenant, 'surpass_V2')
    || tenantIdsEqual(effectiveTenant, 'testwe')
    || tenantIdsEqual(effectiveTenant, 'fullfabric_V2')
    || tenantIdsEqual(effectiveTenant, 'studentcrm');
  const isBlackboardProducts = isBlackboardProductContextTenant(effectiveTenant);
  const isUnit4Products = isUnit4ProductContextTenant(effectiveTenant);
  const isConstructorProducts = isConstructorProductContextTenant(effectiveTenant);
  /* [2026-10-15] access_people — the same fall-through this file already carries for the other
   * lens-category tenants. Its options and derive helper were already in accountTargetProducts.js;
   * the page simply never named the tenant, so the Product column did not render for it at all. */
  const isAccessPeopleProducts = isAccessPeopleProductContextTenant(effectiveTenant);
  /* [2026-10-18] 4science — CRIS / REPOSITORY / GLAM, its three lens categories. */
  const isFourScienceProducts = isFourScienceProductContextTenant(effectiveTenant);
  /* [2026-10-18] testwe — ONLINE_EXAMS / PROCTORING / MARKING, its three lens categories. */
  const isTestweProducts = isTestweProductContextTenant(effectiveTenant);
  const showTargetProductsColumn =
    isSysExamTenant || isLearnwise || isCayuse
    || isBlackboardProducts || isUnit4Products || isConstructorProducts || isAccessPeopleProducts
    || isFourScienceProducts || isTestweProducts;
  const deriveTenantTargetProductValue = isSysExamTenant
    ? deriveEvasysTargetProductValue
    : isLearnwise
      ? deriveLearnwiseTargetProductValue
      : isBlackboardProducts
        ? deriveBlackboardTargetProductValue
        : isUnit4Products
          ? deriveUnit4TargetProductValue
          : isConstructorProducts
            ? deriveConstructorTargetProductValue
            : isAccessPeopleProducts
              ? deriveAccessPeopleTargetProductValue
              : isFourScienceProducts
                ? deriveFourScienceTargetProductValue
                : isTestweProducts
                  ? deriveTestweTargetProductValue
                  : deriveCayuseTargetProductValue;
  const tenantTargetProductOptions = isSysExamTenant
    ? EVASYS_TARGET_PRODUCT_OPTIONS
    : isLearnwise
      ? LEARNWISE_TARGET_PRODUCT_OPTIONS
      : isBlackboardProducts
        ? BLACKBOARD_TARGET_PRODUCT_OPTIONS
        : isUnit4Products
          ? UNIT4_TARGET_PRODUCT_OPTIONS
          : isConstructorProducts
            ? CONSTRUCTOR_TARGET_PRODUCT_OPTIONS
            : isAccessPeopleProducts
              ? ACCESS_PEOPLE_TARGET_PRODUCT_OPTIONS
              : isFourScienceProducts
                ? FOURSCIENCE_TARGET_PRODUCT_OPTIONS
                : isTestweProducts
                  ? TESTWE_TARGET_PRODUCT_OPTIONS
                  : CAYUSE_TARGET_PRODUCT_OPTIONS;
  /* [2026-10-13] Product sits immediately RIGHT OF THE ACCOUNT NAME for the two multi-product
   * tenants whose keys ARE their Market lens categories (Tiago). Blackboard previously led with it
   * in column one; unit4 did not show it at all. Reading the account then its product is the scan
   * order he asked for, and it keeps the two tenants consistent with each other.
   * Every other tenant keeps the existing order (account, tier, target products), so nothing moves
   * for evasys, learnwise or cayuse. */
  const targetProductsAfterAccount =
    isBlackboardProducts || isUnit4Products || isConstructorProducts || isAccessPeopleProducts
    || isFourScienceProducts || isTestweProducts;

  /* ONE definition rendered in one of two positions. Duplicating this JSX to move the column was
   * the obvious route and the wrong one: the switcher owns a write (saveTargetProduct), and two
   * copies of a write drift. */
  const renderTargetProductCell = (row) => (
                          <TableCell
                            className="text-center align-middle"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div
                              className="flex justify-center"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                              }}
                            >
                              <Select
                                value={deriveTenantTargetProductValue(row.target_products) || 'none'}
                                onValueChange={(v) =>
                                  saveTargetProduct(row, v === 'none' ? null : v)
                                }
                                disabled={savingTargetProductId === (row.id || row.account_id)}
                              >
                                <SelectTrigger className="h-8 min-w-[5.75rem] w-auto max-w-[9rem] text-xs">
                                  <SelectValue placeholder="—" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="none">
                                    <span className="text-gray-500">—</span>
                                  </SelectItem>
                                  {tenantTargetProductOptions.map((opt) => (
                                    <SelectItem key={opt.value} value={opt.value}>
                                      {opt.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          </TableCell>
  );
  const [savingTargetProductId, setSavingTargetProductId] = useState(null);
  const [removeTarget, setRemoveTarget] = useState(null);
  const [removeBusy, setRemoveBusy] = useState(false);
  const { status: pilotStatus, pilotMode } = usePilotStatus(effectiveTenant);
  const showRemoveAction = canShowRemoveAccountAction(
    effectiveTenant,
    null,
    Boolean(pilotMode || (pilotStatus == null && isMarketModuleTenantId(effectiveTenant))),
  );

  useEffect(() => {
    const loadCached = async () => {
      if (!contextUser) return;
      const cached = readAccountsCache(effectiveTenant);
      if (cached) {
        console.log('📦 Accounts: Hydrating from cache for tenant:', effectiveTenant);
        
        // Build tenantMatches function to filter cached accounts
        const tenantMatches = (record) => {
          if (!record) return false;
          if (effectiveTenant === 'smowl') return record.tenant_id === 'smowl';
          if (record.tenant_id === effectiveTenant || record.tenant_id === 'global') return true;
          if (Array.isArray(record.visible_to_tenants) && record.visible_to_tenants.includes(effectiveTenant)) return true;
          if (record.tenant_overrides && record.tenant_overrides[effectiveTenant]) return true;
          if (effectiveTenant === 'instructure' && (record.tenant_id === 'default' || !record.tenant_id || record.tenant_id === 'clairelabs')) return true;
          if (effectiveTenant === 'diambra' && (record.tenant_id === 'smowl' || record.tenant_id === 'instructure' || record.tenant_id === 'clairelabs' || record.tenant_id === 'base44' || record.tenant_id === 'default')) return true;
          if (effectiveTenant === 'edqwest') return record.tenant_id === 'edqwest' || record.tenant_id === 'global';
          if (effectiveTenant === 'calmedica') return record.tenant_id === 'calmedica' || record.tenant_id === 'global';
          if (effectiveTenant === 'sebit') return record.tenant_id === 'sebit' || record.tenant_id === 'global';
          if (effectiveTenant === 'prowise') return record.tenant_id === 'prowise' || record.tenant_id === 'global';
          if (effectiveTenant === 'skillwell') return record.tenant_id === 'skillwell' || record.tenant_id === 'skillwise' || record.tenant_id === 'global';
          if (effectiveTenant === 'kairadigital') return record.tenant_id === 'kairadigital';
          if (effectiveTenant === 'plataformaa') return record.tenant_id === 'plataformaa';
          if (effectiveTenant === 'learnwise') return record.tenant_id === 'learnwise' || record.tenant_id === 'global';
          return false;
        };
        
        // Filter cached accounts to ensure tenant isolation
        const filteredAccounts = (cached.accounts || []).filter(tenantMatches);
        const filteredMomentum = (cached.momentumScores || []).filter(
          (m) => m?.tenant_id === 'global_temp' || tenantMatches(m) || tenantIdsEqual(m?.tenant_id, effectiveTenant),
        );
        const filteredDeals = (cached.deals || []).filter(tenantMatches);
        const filteredPeople = (cached.people || []).filter(tenantMatches);
        
        if (filteredAccounts.length !== (cached.accounts || []).length) {
          console.warn('⚠️ Accounts: Filtered out', (cached.accounts || []).length - filteredAccounts.length, 'accounts from cache that don\'t match tenant:', effectiveTenant);
        }

        const accountsWithMomentum = attachMomentumRollingSum(filteredAccounts, filteredMomentum, MOMENTUM_WINDOW_WEEKS);
        
        setAllAccounts(accountsWithMomentum);
        setDepartments(cached.departments || []);
        setMomentumScores(filteredMomentum);
        setDeals(filteredDeals);
        setPeople(filteredPeople);
        setHydratedFromCache(true);
        setIsLoading(false);
        dataLoadedRef.current = true;
        lastLoadedTenantRef.current = effectiveTenant;
      }
    };
    loadCached();
  }, [currentTenant, contextUser, effectiveTenant]);

  const loadData = useCallback(async () => {
    if (!contextUser) return;
    if (loadAttemptedRef.current) {
      console.log('⏭️ Load already in progress, skipping');
      return;
    }

    if (dataLoadedRef.current && 
        allAccounts.length > 0 && 
        lastLoadedTenantRef.current === effectiveTenant) {
      console.log('✅ Data already loaded for current tenant, skipping');
      return;
    }

    console.log('🔄 Accounts page: Starting data load for tenant:', effectiveTenant);
    loadAttemptedRef.current = true;

    if (isInRateLimitCooldown()) {
      const cached = readAccountsCache(effectiveTenant);
      if (cached && cached.accounts && cached.accounts.length > 0) {
        const remaining = getRemainingCooldownTime();
        console.log(`⏸️ In cooldown (${formatRemainingTime(remaining)}), using cached data`);
        
        // Build tenantMatches function to filter cached accounts
        const tenantMatches = (record) => {
          if (!record) return false;
          if (effectiveTenant === 'smowl') return record.tenant_id === 'smowl';
          if (record.tenant_id === effectiveTenant || record.tenant_id === 'global') return true;
          if (Array.isArray(record.visible_to_tenants) && record.visible_to_tenants.includes(effectiveTenant)) return true;
          if (record.tenant_overrides && record.tenant_overrides[effectiveTenant]) return true;
          if (effectiveTenant === 'instructure' && (record.tenant_id === 'default' || !record.tenant_id || record.tenant_id === 'clairelabs')) return true;
          if (effectiveTenant === 'diambra' && (record.tenant_id === 'smowl' || record.tenant_id === 'instructure' || record.tenant_id === 'clairelabs' || record.tenant_id === 'base44' || record.tenant_id === 'default')) return true;
          if (effectiveTenant === 'edqwest') return record.tenant_id === 'edqwest' || record.tenant_id === 'global';
          if (effectiveTenant === 'calmedica') return record.tenant_id === 'calmedica' || record.tenant_id === 'global';
          if (effectiveTenant === 'sebit') return record.tenant_id === 'sebit' || record.tenant_id === 'global';
          if (effectiveTenant === 'prowise') return record.tenant_id === 'prowise' || record.tenant_id === 'global';
          if (effectiveTenant === 'skillwell') return record.tenant_id === 'skillwell' || record.tenant_id === 'skillwise' || record.tenant_id === 'global';
          if (effectiveTenant === 'kairadigital') return record.tenant_id === 'kairadigital';
          if (effectiveTenant === 'plataformaa') return record.tenant_id === 'plataformaa';
          if (effectiveTenant === 'learnwise') return record.tenant_id === 'learnwise' || record.tenant_id === 'global';
          return false;
        };
        
        // Filter cached accounts to ensure tenant isolation
        const filteredAccounts = (cached.accounts || []).filter(tenantMatches);
        const filteredMomentum = (cached.momentumScores || []).filter(
          (m) => m?.tenant_id === 'global_temp' || tenantMatches(m) || tenantIdsEqual(m?.tenant_id, effectiveTenant),
        );
        const filteredDeals = (cached.deals || []).filter(tenantMatches);
        const filteredPeople = (cached.people || []).filter(tenantMatches);
        
        if (filteredAccounts.length !== (cached.accounts || []).length) {
          console.warn('⚠️ Accounts: Filtered out', (cached.accounts || []).length - filteredAccounts.length, 'accounts from cache that don\'t match tenant:', effectiveTenant);
        }

        const accountsWithMomentumCached = attachMomentumRollingSum(filteredAccounts, filteredMomentum, MOMENTUM_WINDOW_WEEKS);
        
        setAllAccounts(accountsWithMomentumCached);
        setDepartments(cached.departments || []);
        setMomentumScores(filteredMomentum);
        setDeals(filteredDeals);
        setPeople(filteredPeople);
        setHydratedFromCache(true);
        setIsLoading(false);
        dataLoadedRef.current = true;

        if (shouldShowRateLimitToast()) {
          toast({
            title: "⏸️ Rate limit active",
            description: `Using cached data. Will retry in ${formatRemainingTime(remaining)}.`,
            variant: "default",
            duration: 8000
          });
          markRateLimitToastShown();
        }
        loadAttemptedRef.current = false;
        return;
      }
    }

    if (!hydratedFromCache && allAccounts.length === 0) {
      setIsLoading(true);
    }

    try {
      console.log('🔄 Fetching essential data first...');
      
      const tenantFilter = buildTenantFilter(effectiveTenant);
      
      const tenantMatches = (record) => {
        if (!record) return false;
        if (effectiveTenant === 'smowl') return record.tenant_id === 'smowl';
        if (record.tenant_id === effectiveTenant || record.tenant_id === 'global') {
          return true;
        }
        
        if (effectiveTenant === 'instructure' && (record.tenant_id === 'default' || !record.tenant_id)) {
          return true;
        }
        
        if (effectiveTenant === 'instructure' && record.tenant_id === 'clairelabs') {
          return true;
        }
        
        if (Array.isArray(record.visible_to_tenants) && record.visible_to_tenants.includes(effectiveTenant)) {
          return true;
        }
        
        if (record.tenant_overrides && record.tenant_overrides[effectiveTenant]) {
          return true;
        }
        
        if (effectiveTenant === 'diambra' && 
            (record.tenant_id === 'smowl' || record.tenant_id === 'instructure' || record.tenant_id === 'clairelabs' || record.tenant_id === 'base44' || record.tenant_id === 'default')) {
          return true;
        }
        
        if (effectiveTenant === 'edqwest') return record.tenant_id === 'edqwest';
        if (effectiveTenant === 'faria') return record.tenant_id === 'edqwest';
        
        if (effectiveTenant === 'calmedica') return record.tenant_id === 'calmedica' || record.tenant_id === 'global';
        if (effectiveTenant === 'sebit') return record.tenant_id === 'sebit' || record.tenant_id === 'global';
        if (effectiveTenant === 'prowise') return record.tenant_id === 'prowise' || record.tenant_id === 'global';
        if (effectiveTenant === 'skillwell') return record.tenant_id === 'skillwell' || record.tenant_id === 'skillwise' || record.tenant_id === 'global';
        if (effectiveTenant === 'kairadigital') return record.tenant_id === 'kairadigital';
        if (effectiveTenant === 'plataformaa') return record.tenant_id === 'plataformaa';
        if (effectiveTenant === 'learnwise') return record.tenant_id === 'learnwise' || record.tenant_id === 'global';
        
        return false;
      };

      // 🔴 NO 300-ROW CAP. The cap was applied BEFORE the client-side tenantMatches
      // filter below, and ordered by account_fit_score — a field that is null on the
      // overwhelming majority of rows. So the page fetched 300 arbitrary accounts and
      // then narrowed them, which silently hid entitled accounts: constructor_tech
      // could not see the Association of Taxation Technicians or the Royal College of
      // Psychiatrists at all. A limit that precedes the tenant filter is not a page
      // size, it is a correctness bug.
      const accountDataRaw = await fetchWithRetry(() => AccountFinal.filter({ tenant_id: tenantFilter }, "-account_fit_score", 10000)).catch(() => []);
      
      let accounts = (accountDataRaw || []).filter(tenantMatches).map(acc => {
        if (acc.tenant_overrides && acc.tenant_overrides[effectiveTenant]) {
          return { ...acc, ...acc.tenant_overrides[effectiveTenant] };
        }
        return acc;
      });

      accounts = accounts.filter(acc => acc.is_visible !== false);
      
      const accountsForInitialRender = accounts.map(acc => ({
        ...acc,
        momentum_4w: 0
      }));

      clearRateLimitCooldown();

      if (!aliveRef.current) return;

      startTransition(() => {
        if (!aliveRef.current) return;
        setAllAccounts(accountsForInitialRender);
        
        writeAccountsCache(effectiveTenant, {
          accounts: accountsForInitialRender,
          departments: [],
          deals: [],
          momentumScores: [],
          people: []
        });
        
        setIsLoading(false);
        setHydratedFromCache(true);
        dataLoadedRef.current = true;
        lastLoadedTenantRef.current = effectiveTenant;
      });

      console.log('🔄 Kicking off background data load for deals, momentum, people, and potentially departments...');

      const dataPromises = [
        fetchWithRetry(() => Deal.filter({ tenant_id: tenantFilter }, "", 200)).catch(() => []),
        fetchWithRetry(async () => {
          const [tenantRows, globalTempRows] = await Promise.all([
            MomentumScore.filter({ tenant_id: tenantFilter }, "-weekly_period", 500),
            MomentumScore.filter({ tenant_id: 'global_temp' }, "-weekly_period", 300),
          ]);
          return [...(tenantRows || []), ...(globalTempRows || [])];
        }).catch(() => []),
        fetchWithRetry(() => PersonFinal.filter({ tenant_id: tenantFilter }, "-persona_fit_score", 200)).catch(() => [])
      ];
      
      if (effectiveTenant === 'calmedica') {
        dataPromises.push(fetchWithRetry(() => Department.filter({}, "", 300)));
      }

      Promise.all(dataPromises).then((results) => {
        if (!aliveRef.current) return;
        
        const [dealData, momentumDataRaw, peopleData] = results;
        
        const deals = (dealData || []).filter(tenantMatches);
        const momentum = (momentumDataRaw || [])
          .filter((m) => m?.tenant_id === 'global_temp' || tenantMatches(m) || tenantIdsEqual(m?.tenant_id, effectiveTenant))
          .map(m => ({
          ...m,
          momentum_score_change: sanitizeMomentumChange(m.momentum_score_change)
        }));
        const people = (peopleData || []).filter(tenantMatches).map(p => {
          if (p.tenant_overrides && p.tenant_overrides[effectiveTenant]) {
            return { ...p, ...p.tenant_overrides[effectiveTenant] };
          }
          return p;
        });
        
        let departments = [];
        if (effectiveTenant === 'calmedica') {
          // Use Department entity data from results[3]
          const departmentsData = results[3];
          if (departmentsData && departmentsData.length > 0) {
            departments = departmentsData
              .filter(d => tenantMatches(d))
              .map(d => applyTenantOverrides(d, effectiveTenant));
              console.log('🏥 Loaded departments from Department entity:', departments.length);
              console.log('🏥 Department sample data:', departments.slice(0, 3).map(d => ({
                account_name: d.account_name,
                department: d.department,
                department_fit_score: d.department_fit_score,
                score_type: typeof d.department_fit_score
              })));
          } else {
            console.log('🏥 No departments found in Department entity, check if table exists');
          }
        }
      
        const accountsWithFinalMomentum = attachMomentumRollingSum(accounts, momentum, MOMENTUM_WINDOW_WEEKS);

        const accountsWithFinalOverrides = accountsWithFinalMomentum.map(acc => 
          applyTenantOverrides(acc, effectiveTenant)
        );

        startTransition(() => {
          if (!aliveRef.current) return;
          setAllAccounts(accountsWithFinalOverrides);
          setDepartments(departments);
          setDeals(deals);
          setMomentumScores(momentum);
          setPeople(people);
          
          writeAccountsCache(effectiveTenant, {
            accounts: accountsWithFinalOverrides,
            departments,
            deals,
            momentumScores: momentum,
            people
          });
          
          console.log('✅ Accounts page background data loaded for tenant:', effectiveTenant, {
            accounts: `${accountsWithFinalOverrides.length}/${accountDataRaw.length}`,
            departments: departments.length,
            deals: `${deals.length}/${dealData.length}`,
            momentum: `${momentum.length}/${momentumDataRaw.length}`,
            people: `${people.length}/${peopleData.length}`
          });
        });

      }).catch(err => {
        console.warn('Background data load failed:', err);
      });

    } catch (error) {
      if (!aliveRef.current) return;
      console.error("❌ Error loading accounts:", error);
      if (error.response?.status === 429 || (error.message && error.message.includes('Rate limit'))) {
        setRateLimitCooldown();
        toast?.({ 
          title: "Rate limit reached", 
          description: "Showing cached data. Please wait a moment.", 
          variant: "default" 
        });
      }
      setIsLoading(false);
    } finally {
      loadAttemptedRef.current = false;
    }
  }, [hydratedFromCache, currentTenant, allAccounts.length, toast, contextUser, effectiveTenant]);

  useEffect(() => {
    aliveRef.current = true;
    loadData();
    return () => { aliveRef.current = false; };
  }, [loadData]);

  // Get unique departments for filter
  const availableDepartments = useMemo(() => {
    if (!isCalmedica) return [];
    const deptSet = new Set();
    departments.forEach(dept => {
      if (dept.department) deptSet.add(dept.department);
    });
    return Array.from(deptSet).sort();
  }, [departments, isCalmedica]);

  // UPDATED: Build account-to-departments mapping - now creates rows for display
  const accountDepartmentRows = useMemo(() => {
    if (!isCalmedica) return [];
    
    const rows = [];
    
    // For each account
    allAccounts.forEach(acc => {
      // Find all departments for this account
      const accountDepts = departments.filter(dept => {
        const matchesById = dept.account_id && 
          (String(acc.id) === String(dept.account_id) || String(acc.account_id) === String(dept.account_id));
        const matchesByName = dept.account_name && acc.account_name === dept.account_name;
        return matchesById || matchesByName;
      });
      
      // Sort departments by fit score descending
      const sortedDepts = accountDepts.sort((a, b) => 
        (Number(b.department_fit_score) || 0) - (Number(a.department_fit_score) || 0)
      );

      // After: const sortedDepts = accountDepts.sort(...)
console.log('🔍 Account matching debug:', {
  account: acc.account_name,
  accountId: acc.account_id,
  deptMatchCount: accountDepts.length,
  sampleDept: accountDepts[0] ? {
    dept: accountDepts[0].department,
    score: accountDepts[0].department_fit_score,
    deptAccountId: accountDepts[0].account_id,
    deptAccountName: accountDepts[0].account_name
  } : null
});

      // Calmedica: still list the account when there is no department row or no fit data yet
      if (sortedDepts.length === 0) {
        rows.push({
          ...acc,
          department: null,
          department_fit_score: null,
          department_fit_rationale: null,
          department_id: null,
          rowId: `${acc.id}-no-department`,
        });
      } else {
        // Create one row per department (up to 8)
        sortedDepts.slice(0, 8).forEach((dept) => {
          rows.push({
            ...acc, // All account fields
            department: dept.department,
            department_fit_score: dept.department_fit_score,
            department_fit_rationale: dept.department_fit_rationale,
            department_id: dept.id,
            // Create a unique row ID combining account and department
            rowId: `${acc.id}-${dept.department}`,
          });
        });
      }
    });
    
    console.log('🗺️ Account-department rows created:', {
      totalRows: rows.length,
      sample: rows.slice(0, 3).map(r => ({
        account: r.account_name,
        department: r.department,
        score: r.department_fit_score
      }))
    });
    
    return rows;
  }, [departments, allAccounts, isCalmedica]);

  // UPDATED: For non-calmedica, use normal accounts; for calmedica, use expanded rows
  const baseDataForFiltering = useMemo(() => {
    return isCalmedica ? accountDepartmentRows : allAccounts;
  }, [isCalmedica, accountDepartmentRows, allAccounts]);

  // UPDATED: Filter logic now works on rows for calmedica
  const filteredData = useMemo(() => {
    let filtered = baseDataForFiltering.filter(item =>
      (item.account_name.toLowerCase().includes(searchTerm.toLowerCase())) &&
      (countryFilter === "all" || item.country === countryFilter)
    );
    
    // For calmedica, filter by department
    if (isCalmedica && departmentFilter !== "all") {
      filtered = filtered.filter(row => row.department === departmentFilter);
      
      console.log('🏥 Department filter applied:', {
        department: departmentFilter,
        rowsMatched: filtered.length
      });
    }
    
    return filtered;
  }, [baseDataForFiltering, searchTerm, countryFilter, departmentFilter, isCalmedica]);

  const availableCountries = useMemo(() => {
    const countries = [...new Set(allAccounts.map(a => a.country).filter(Boolean))];
    return countries.sort();
  }, [allAccounts]);
  
  const hasIncumbentSentiment = useMemo(() => {
    return filteredData.some(item => item.incumbent_sentiment);
  }, [filteredData]);

  const showDecisionWindowColumn = useMemo(() => {
    if (showDecisionWindowForTenant) return true;
    return allAccounts.some(accountHasDecisionWindowValue);
  }, [allAccounts, showDecisionWindowForTenant]);

  const showSubscribersColumn = useMemo(() => {
    return datasetHasSubscribers(filteredData);
  }, [filteredData]);
  
  // UPDATED: Sorting now works on the filtered data (rows for calmedica)
  const sortedData = useMemo(() => {
    let sortableItems = [...filteredData];
    if (sortConfig.key !== null) {
      sortableItems.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        if (sortConfig.key === 'target_products' && showTargetProductsColumn) {
          aValue = deriveTenantTargetProductValue(a.target_products) || '';
          bValue = deriveTenantTargetProductValue(b.target_products) || '';
        }

        if (sortConfig.key === 'account_tier') {
          aValue = tierSortNumber(a.account_tier || a.tier);
          bValue = tierSortNumber(b.account_tier || b.tier);
        }

        if (sortConfig.key === 'subscribers') {
          aValue = a.subscribers;
          bValue = b.subscribers;
        }
        
        if (sortConfig.key === 'decision_window_date') {
          aValue = aValue ? new Date(aValue) : null;
          bValue = bValue ? new Date(bValue) : null;
        }
        
        if (aValue === null || aValue === undefined) return 1;
        if (bValue === null || bValue === undefined) return -1;
        
        let comparison = 0;
        if (typeof aValue === 'string' && typeof bValue === 'string') {
          comparison = aValue.localeCompare(bValue);
        } else if (aValue instanceof Date && bValue instanceof Date) {
          comparison = aValue.getTime() - bValue.getTime();
        } else {
          if (aValue < bValue) {
            comparison = -1;
          } else if (aValue > bValue) {
            comparison = 1;
          } else {
            comparison = 0;
          }
        }

        return sortConfig.direction === 'ascending' ? comparison : -comparison;
      });
    }
    return sortableItems;
  }, [filteredData, sortConfig, showTargetProductsColumn, deriveTenantTargetProductValue]);

  const handleSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    } 
    else if (sortConfig.key !== key && (key === 'account_fit_score' || key === 'momentum_4w' || key === 'incumbent_sentiment' || key === 'subscribers')) {
        direction = 'descending';
    }
    if (key === 'account_tier' && sortConfig.key !== key) {
      direction = 'ascending';
    }
    setSortConfig({ key, direction });
  };

  const saveTargetProduct = useCallback(
    async (row, newValue) => {
      const recordId = row.id || row.account_id;
      if (!recordId || !contextUser) return;
      setSavingTargetProductId(recordId);
      try {
        const tenantId = resolveEffectiveTenant(
          contextUser?.role === 'admin' ? localStorage.getItem('admin-active-tenant') : null,
          currentTenant,
          contextUser,
          DEFAULT_TENANT
        );
        const valueToSave = newValue === 'none' || !newValue ? null : newValue;
        const payload = { tenant_id: tenantId, target_products: valueToSave };
        const API_BASE_URL =
          window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
            ? 'https://api.neogtm.com'
            : 'https://api.neogtm.com';
        const headers = {
          'Content-Type': 'application/json',
          'x-tenant-id': tenantId,
          'x-user-id': contextUser?.id || '',
        };
        let res = await fetch(`${API_BASE_URL}/api/analytics-v2/accounts/${recordId}/target-products`, {
          method: 'PATCH',
          headers,
          credentials: 'include',
          body: JSON.stringify(payload),
        });
        let updated;
        if (res.ok) {
          const data = await res.json();
          updated = data.account || data;
        } else if (res.status === 404) {
          const entityRes = await fetch(
            `${API_BASE_URL}/api/entities/AccountFinal/${encodeURIComponent(recordId)}`,
            {
              method: 'PATCH',
              headers,
              credentials: 'include',
              body: JSON.stringify({ target_products: valueToSave }),
            }
          );
          if (!entityRes.ok) {
            const errJson = await entityRes.json().catch(() => ({}));
            throw new Error(errJson.error || errJson.message || 'Save failed');
          }
          updated = await entityRes.json();
        } else {
          const errJson = await res.json().catch(() => ({}));
          throw new Error(errJson.error || errJson.message || 'Save failed');
        }
        const nextTp = updated.target_products ?? null;
        const matchId = (a) =>
          String(a.id) === String(recordId) || String(a.account_id) === String(recordId);
        setAllAccounts((prev) => prev.map((a) => (matchId(a) ? { ...a, target_products: nextTp } : a)));
        toast({ title: 'Saved', description: 'Target product updated' });
      } catch (e) {
        console.error(e);
        toast({ title: 'Error', description: e?.message || 'Could not save', variant: 'destructive' });
      } finally {
        setSavingTargetProductId(null);
      }
    },
    [contextUser, currentTenant, toast]
  );

  const handleTierUpdated = useCallback((recordId, newTier) => {
    const matchId = (a) =>
      String(a.id) === String(recordId) || String(a.account_id) === String(recordId);
    setAllAccounts((prev) =>
      prev.map((a) =>
        matchId(a) ? { ...a, account_tier: newTier, tier: newTier } : a,
      ),
    );
  }, []);

  const confirmRemoveFromList = useCallback(async () => {
    const row = removeTarget;
    const workingId = row?.account_id || row?.id;
    if (!workingId || !effectiveTenant) return;
    const matchRemoved = (a) =>
      (row.id && String(a.id) === String(row.id)) ||
      (row.account_id && String(a.account_id) === String(row.account_id));
    const previous = allAccounts;
    setAllAccounts((prev) => prev.filter((a) => !matchRemoved(a)));
    setRemoveBusy(true);
    try {
      const result = await removeMarketAccount(effectiveTenant, { account_id: workingId });
      const interpreted = interpretRemoveAccountResult(result);
      if (!interpreted.ok) {
        setAllAccounts(previous);
        notify({ title: interpreted.message, variant: 'destructive' });
        return;
      }
      clearAccountPageCache(effectiveTenant, row.id);
      clearHomeDestinationCache();
      notify({
        title: `Removed ${row.account_name || 'account'} from your list`,
        description: 'You can add it back any time from Market.',
      });
      setRemoveTarget(null);
    } catch (e) {
      setAllAccounts(previous);
      notify({
        title: 'Could not remove account',
        description: e?.message || 'Please try again',
        variant: 'destructive',
      });
    } finally {
      setRemoveBusy(false);
    }
  }, [removeTarget, effectiveTenant, allAccounts]);
  
  const SortableHeader = ({ children, sortKey, className = '' }) => (
    <TableHead onClick={() => handleSort(sortKey)} className={`cursor-pointer hover:bg-gray-100 transition-colors text-[#5C6B7A] text-center ${className}`}>
      <div className="flex items-center justify-center gap-2">
        {sortConfig.key === sortKey ? (
          sortConfig.direction === 'ascending' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
        ) : (
          <span className="w-3 h-3"></span>
        )}
        {children}
      </div>
    </TableHead>
  );

  const showSkeletons = isLoading && !hydratedFromCache;

  return (
    <AccountPlaybookStatusProvider accounts={allAccounts} currentTenant={currentTenant}>
      <div className="bg-gray-50 -m-4 md:-m-8 -mt-4 md:-mt-8 min-h-screen overflow-x-hidden">
        <div className="bg-white border-b mb-6 -mx-4 md:-mx-8">
          <div className="max-w-7xl mx-auto px-12 sm:px-6 md:px-10 py-6 md:py-8">
            <h1 className="text-3xl font-bold text-[#0A1A2F]">{t('accounts')}</h1>
            <p className="text-lg text-[#5C6B7A]">{t('manageExploreInstitutions')}</p>
          </div>
        </div>
      <div className="max-w-7xl mx-auto px-2 sm:px-6 md:px-6 py-0">

        <div className="mb-6">
          <div className="flex flex-col md:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                placeholder={t('searchAccounts')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 text-sm"
              />
            </div>
            {!isFaria && availableCountries.length > 0 && (
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-[#5C6B7A] hidden md:block" />
                <Select value={countryFilter} onValueChange={setCountryFilter}>
                  <SelectTrigger className="w-full md:w-40 text-sm">
                    <SelectValue placeholder={t('filterByCountry')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('allCountries')}</SelectItem>
                    {availableCountries.map(country => (
                      <SelectItem key={country} value={country}>{country}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {isCalmedica && availableDepartments.length > 0 && (
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-[#5C6B7A] hidden md:block" />
                <Select value={departmentFilter} onValueChange={setDepartmentFilter}>
                  <SelectTrigger className="w-full md:w-48 text-sm">
                    <SelectValue placeholder={t('filterByDepartment')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('allDepartments')}</SelectItem>
                    {availableDepartments.map(dept => (
                      <SelectItem key={dept} value={dept}>{dept}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </div>

        <div className="hidden md:block border rounded-lg shadow-md overflow-visible">
          {showSkeletons ? (
            <div className="p-6">
              <div className="h-64 bg-gray-100 rounded-md animate-pulse" />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50 hover:bg-gray-50">
                    <SortableHeader sortKey="account_name">{t('account')}</SortableHeader>
                    {showTargetProductsColumn && targetProductsAfterAccount && (
                      <SortableHeader sortKey="target_products" className="text-center min-w-[7.5rem]">
                        {t('acctsColProduct')}
                      </SortableHeader>
                    )}
                    {showAccountTierColumn && (
                      <SortableHeader sortKey="account_tier" className="text-center min-w-[6.5rem]">
                        {t('acctsColAccountTier')}
                      </SortableHeader>
                    )}
                    {showTargetProductsColumn && !targetProductsAfterAccount && (
                      <SortableHeader sortKey="target_products" className="text-center min-w-[7.5rem]">
                        {t('acctsColTargetProducts')}
                      </SortableHeader>
                    )}
                    {!isFaria && (
                      <SortableHeader sortKey="country" className="text-center min-w-[10.5rem] whitespace-nowrap pl-2">
                        {t('country')}
                      </SortableHeader>
                    )}
                    {showSubscribersColumn && (
                      <SortableHeader sortKey="subscribers" className="text-center min-w-[6rem]">
                        {t('acctsColSubscribers')}
                      </SortableHeader>
                    )}
                    {isCalmedica && (
                      <SortableHeader sortKey="department_fit_score" className="text-center">{t('departmentFitScore')}</SortableHeader>
                    )}
                    {isFaria && (
                      <TableHead className="text-center text-[#5C6B7A] cursor-pointer hover:bg-gray-100 transition-colors" onClick={() => handleSort('faria_products')}>
                        <div className="flex items-center justify-center gap-2">
                          {sortConfig.key === 'faria_products' ? (
                            sortConfig.direction === 'ascending' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                          ) : (
                            <span className="w-3 h-3"></span>
                          )}
                          {t('fariaProducts')}
                        </div>
                      </TableHead>
                    )}
                    <SortableHeader sortKey="account_fit_score" className="text-center">
                      {t('accountFitScore')}
                    </SortableHeader>
                    {hasIncumbentSentiment && (
                      <SortableHeader sortKey="incumbent_sentiment" className="text-center">
                        {t('incumbentSentiment')}
                      </SortableHeader>
                    )}
                    {showDecisionWindowColumn && <SortableHeader sortKey="decision_window_date" className="text-center">{t('decisionWindow')}</SortableHeader>}
                    {isFaria && <SortableHeader sortKey="renewal_risk" className="text-center">{t('renewalRisk')}</SortableHeader>}
                    {isFaria && <TableHead className="text-center text-[#5C6B7A]">{t('expansionOpps')}</TableHead>}
                    <SortableHeader sortKey="momentum_4w" className="text-center">{t('momentum')} (4w)</SortableHeader>
                    <TableHead className="text-center text-[#5C6B7A]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedData.map((row) => {
                    const decisionWindow = getAccountDecisionWindowDisplay(row);
                    const hasDecisionWindowValue = accountHasDecisionWindowValue(row);
                    
                    const isCHUBordeaux = isCalmedica && row.account_name === 'CHU de Bordeaux';
                    const rowKey = isCalmedica ? row.rowId : row.id;
                    
                    return (
                      <TableRow key={rowKey} className="hover:bg-gray-50">
                        <TableCell className="text-left">
                          <Link to={createPageUrl(`AccountFinal?id=${row.id}`)} className="flex items-center gap-3 hover:text-[#00C2C2]">
                            {!isUntrustedAccountLogoUrl(row.logo_url) ? (
                              <img 
                                src={row.logo_url} 
                                alt={row.account_name}
                                className="w-10 h-10 object-contain rounded-md border p-0.5 bg-white flex-shrink-0"
                                referrerPolicy="no-referrer"
                              />
                            ) : (
                              <div className="w-10 h-10 rounded-md bg-gray-200 flex items-center justify-center flex-shrink-0">
                                <span className="text-xs font-semibold text-[#5C6B7A]">
                                  {accountNameInitials(row.account_name)}
                                </span>
                              </div>
                            )}
                            <div className="flex items-center gap-2">
                              <span className="font-semibold">{row.account_name}</span>
                              {row.is_client === true && <ExistingCustomerIcon />}
                              {isListedForSale(row) && <ListedForSaleBadge account={row} stopPropagation />}
                              <AccountPlaybookMarker accountId={row.id} />
                              {isCHUBordeaux && (
                                <TooltipProvider delayDuration={0}>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <div className="cursor-help">
                                        <Badge className="bg-blue-500 text-white text-xs">
                                          client
                                        </Badge>
                                      </div>
                                    </TooltipTrigger>
                                    <TooltipContent 
                                      side="top"
                                      className="bg-[#0A1A2F] text-white border-0 shadow-lg"
                                    >
                                      <p className="text-xs">Ambulatory Surgery client</p>
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              )}
                            </div>
                          </Link>
                        </TableCell>
                        {showTargetProductsColumn && targetProductsAfterAccount && renderTargetProductCell(row)}
                        {showAccountTierColumn && (
                          <TableCell
                            className="text-center align-middle"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div
                              className="flex justify-center"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                              }}
                            >
                              <AccountTierSelect
                                accountId={row.account_id || row.id}
                                entityId={row.id}
                                tier={row.account_tier || row.tier}
                                tenantId={effectiveTenant}
                                userId={contextUser?.id}
                                onTierUpdate={(newTier) =>
                                  handleTierUpdated(row.account_id || row.id, newTier)
                                }
                                triggerClassName="h-8 w-auto min-w-[5.5rem] border-0 p-0 hover:opacity-80"
                              />
                            </div>
                          </TableCell>
                        )}
                        {showTargetProductsColumn && !targetProductsAfterAccount && renderTargetProductCell(row)}
                        {!isFaria && (
                          <TableCell className="text-center min-w-[10.5rem] whitespace-nowrap pl-2 text-[#0A1A2F]">
                            {row.country}
                          </TableCell>
                        )}
                        {showSubscribersColumn && (
                          <TableCell className="text-center text-[#0A1A2F]">
                            {row.subscribers != null ? (
                              <TooltipProvider delayDuration={0}>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span className="cursor-default">{formatAccountCount(row.subscribers)}</span>
                                  </TooltipTrigger>
                                  <TooltipContent side="top" className="text-xs">
                                    Subscribers
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            ) : null}
                          </TableCell>
                        )}
                        {isCalmedica && (
                          <TableCell className="text-center">
                            {row.department ? (
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <div className="cursor-help inline-block">
                                      <Badge className={`text-xs ${
                                        row.department_fit_score >= 90 ? 'bg-green-100 text-green-700' :
                                        row.department_fit_score >= 75 ? 'bg-blue-100 text-blue-700' :
                                        row.department_fit_score >= 60 ? 'bg-yellow-100 text-yellow-700' :
                                        'bg-gray-100 text-gray-700'
                                      }`}>
                                        {row.department} ({row.department_fit_score})
                                      </Badge>
                                    </div>
                                  </TooltipTrigger>
                                  <TooltipContent 
                                    side="bottom"
                                    className="w-[400px] bg-[#0A1A2F] text-white border-0 shadow-lg p-3"
                                  >
                                    <p className="text-sm font-semibold text-[#00C2C2] mb-1">{row.department}</p>
                                    <p className="text-xs leading-relaxed">
                                      {row.department_fit_rationale || t('noRationaleAvailable')}
                                    </p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            ) : (
                              <span className="text-xs text-gray-400">—</span>
                            )}
                          </TableCell>
                        )}
                        {isFaria && (
                          <TableCell className="text-center">
                            {row.faria_products ? (
                              <div className="flex justify-center">
                                <span className="text-xs">{row.faria_products}</span>
                              </div>
                            ) : (
                              <span className="text-xs text-gray-400">—</span>
                            )}
                          </TableCell>
                        )}
                        <TableCell className="text-center">
                          <div className="flex justify-center">
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div className="inline-block cursor-help">
                                    <AccountFitBadge account={row} />
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent 
                                  side="bottom" 
                                  align="center"
                                  sideOffset={8}
                                  className="w-[600px] bg-[#0A1A2F] text-white border-0 shadow-lg z-[9999]"
                                >
                                  <p className="text-sm font-semibold text-[#00C2C2] mb-1">{t('scoreRationale')}</p>
                                  <p className="text-xs leading-relaxed">{row.score_rationale || t('noRationaleAvailable')}</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </div>
                        </TableCell>
                        {hasIncumbentSentiment && (
                          <TableCell className="text-center">
                            <div className="flex justify-center">
                              {row.incumbent_sentiment ? (
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <div className="inline-block cursor-help">
                                        <Badge className="bg-purple-50 text-purple-700 border border-purple-200 text-xs">
                                          {translateIncumbentSentiment(language, row.incumbent_sentiment)}
                                        </Badge>
                                      </div>
                                    </TooltipTrigger>
                                    <TooltipContent 
                                      side="bottom" 
                                      align="center"
                                      sideOffset={8}
                                      className="w-[600px] bg-[#0A1A2F] text-white border-0 shadow-lg z-[9999]"
                                    >
                                      <p className="text-sm font-semibold text-purple-300 mb-1">{t('incumbentSentiment')}</p>
                                      <p className="text-xs leading-relaxed">
                                        {row.incumbent_sentiment_rationale || t('noRationaleAvailable')}
                                      </p>
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              ) : (
                                <span className="text-xs text-gray-400">—</span>
                              )}
                            </div>
                          </TableCell>
                        )}
                        {showDecisionWindowColumn && (
                          <TableCell className="text-center">
                            <div className="flex justify-center">
                              {hasDecisionWindowValue && row.decision_window_rationale ? (
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <div className="inline-block cursor-help">
                                        <Badge variant="outline" className="text-xs">
                                          {decisionWindow}
                                        </Badge>
                                      </div>
                                    </TooltipTrigger>
                                    <TooltipContent 
                                      side="bottom" 
                                      align="center"
                                      sideOffset={8}
                                      className="w-[600px] bg-[#0A1A2F] text-white border-0 shadow-lg z-[9999]"
                                    >
                                      <p className="text-sm font-semibold text-[#00C2C2] mb-1">{t('decisionWindowRationale')}</p>
                                      <p className="text-xs leading-relaxed">
                                        {row.decision_window_rationale}
                                      </p>
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              ) : hasDecisionWindowValue ? (
                                <Badge variant="outline" className="text-xs">
                                  {decisionWindow}
                                </Badge>
                              ) : null}
                            </div>
                          </TableCell>
                        )}
                        {isFaria && (
                          <TableCell className="text-center">
                            <div className="flex justify-center">
                              {row.renewal_risk ? (
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <div className="inline-block cursor-help">
                                        <Badge className={`${getRenewalRiskColor(row.renewal_risk)} text-xs`}>
                                          {row.renewal_risk}
                                        </Badge>
                                      </div>
                                    </TooltipTrigger>
                                    <TooltipContent 
                                      side="bottom" 
                                      align="center"
                                      sideOffset={8}
                                      className="w-[600px] bg-[#0A1A2F] text-white border-0 shadow-lg z-[9999]"
                                    >
                                      <p className="text-sm font-semibold text-[#00C2C2] mb-1">{t('riskRationale')}</p>
                                      <p className="text-xs leading-relaxed">{row.risk_rationale || t('noRationaleAvailable')}</p>
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              ) : (
                                <span className="text-xs text-gray-400">—</span>
                              )}
                            </div>
                          </TableCell>
                        )}
                        {isFaria && (
                          <TableCell className="text-center">
                            <div className="flex flex-col gap-1 items-center">
                              {row.expansion_product_1 && (
                                <span className="text-xs">{row.expansion_product_1}</span>
                              )}
                              {row.expansion_product_2 && (
                                <span className="text-xs">{row.expansion_product_2}</span>
                              )}
                              {!row.expansion_product_1 && !row.expansion_product_2 && (
                                <span className="text-xs text-gray-400">—</span>
                              )}
                            </div>
                          </TableCell>
                        )}
                        <TableCell className="text-center">
                            <span className={`text-sm font-medium ${row.momentum_4w > 0 ? 'text-green-600' : row.momentum_4w < 0 ? 'text-red-600' : 'text-gray-500'}`}>
                              {row.momentum_4w > 0 ? `+${row.momentum_4w}` : row.momentum_4w || 0}
                            </span>
                          </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-0.5">
                            {showRemoveAction && (
                              <AccountOverflowMenu onRemove={() => setRemoveTarget(row)} />
                            )}
                            <Link to={createPageUrl(`AccountFinal?id=${row.id}`)}>
                              <ChevronRight className="w-5 h-5 text-gray-400" />
                            </Link>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </div>

        <div className="md:hidden">
          {showSkeletons ? (
            <div className="space-y-3">
              {[1,2,3,4,5].map(i => <div key={i} className="h-20 bg-gray-100 rounded-md animate-pulse" />)}
            </div>
          ) : (
            <div className="space-y-3">
              {sortedData.map((row) => {
                const decisionWindow = getAccountDecisionWindowDisplay(row);
                const hasDecisionWindowValue = accountHasDecisionWindowValue(row);
                
                const isCHUBordeaux = isCalmedica && row.account_name === 'CHU de Bordeaux';
                const rowKey = isCalmedica ? row.rowId : row.id;

                return (
                  <Link key={rowKey} to={createPageUrl(`AccountFinal?id=${row.id}`)}>
                    <Card className="hover:shadow-lg transition-shadow">
                      <CardContent className="p-3">
                        <div className="flex items-center gap-3">
                        {(!isUntrustedAccountLogoUrl(row.logo_url)) ? (
                            <img 
                              src={row.logo_url} 
                              alt={row.account_name}
                              className="w-10 h-10 object-contain rounded-md border p-0.5 bg-white"
                              referrerPolicy="no-referrer"
                            />
                          ) : (
                            <div className="w-10 h-10 rounded-md bg-gray-200 flex items-center justify-center flex-shrink-0">
                              <span className="text-xs font-semibold text-[#5C6B7A]">
                                {accountNameInitials(row.account_name)}
                              </span>
                            </div>
                          )}
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              <h3 className="font-semibold text-[#0A1A2F] text-sm truncate">{row.account_name}</h3>
                              {row.is_client === true && <ExistingCustomerIcon />}
                              {isListedForSale(row) && <ListedForSaleBadge account={row} className="shrink-0" stopPropagation />}
                              <AccountPlaybookMarker accountId={row.id} />
                              {isCHUBordeaux && (
                                <TooltipProvider delayDuration={0}>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <div className="cursor-help">
                                        <Badge className="bg-blue-500 text-white text-xs">
                                          client
                                        </Badge>
                                      </div>
                                    </TooltipTrigger>
                                    <TooltipContent 
                                      side="top"
                                      className="bg-[#0A1A2F] text-white border-0 shadow-lg"
                                    >
                                      <p className="text-xs">Ambulatory Surgery client</p>
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              )}
                            </div>
                            {!isFaria && <p className="text-xs text-[#5C6B7A] whitespace-nowrap">{row.country}</p>}
                            {row.subscribers != null && (
                              <p className="text-xs text-[#5C6B7A]">{formatAccountCount(row.subscribers)} subscribers</p>
                            )}
                            {showAccountTierColumn && (
                              <div
                                className="mt-2"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                }}
                              >
                                <AccountTierSelect
                                  accountId={row.account_id || row.id}
                                  entityId={row.id}
                                  tier={row.account_tier || row.tier}
                                  tenantId={effectiveTenant}
                                  userId={contextUser?.id}
                                  onTierUpdate={(newTier) =>
                                    handleTierUpdated(row.account_id || row.id, newTier)
                                  }
                                />
                              </div>
                            )}
                            {showTargetProductsColumn && (
                              <div
                                className="mt-2 max-w-[200px]"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                }}
                              >
                                <Select
                                  value={deriveTenantTargetProductValue(row.target_products) || 'none'}
                                  onValueChange={(v) =>
                                    saveTargetProduct(row, v === 'none' ? null : v)
                                  }
                                  disabled={savingTargetProductId === (row.id || row.account_id)}
                                >
                                  <SelectTrigger className="h-8 w-full text-xs">
                                    <SelectValue placeholder={t('acctsTargetProductPlaceholder')} />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="none">
                                      <span className="text-gray-500">—</span>
                                    </SelectItem>
                                    {tenantTargetProductOptions.map((opt) => (
                                      <SelectItem key={opt.value} value={opt.value}>
                                        {opt.label}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                            )}
                            {isFaria && row.faria_products && (
                              <p className="text-xs text-[#5C6B7A]">{row.faria_products}</p>
                            )}
                            <div className="flex flex-wrap items-center gap-1 mt-1">
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <div className="inline-block cursor-help">
                                      <AccountFitBadge account={row} />
                                    </div>
                                  </TooltipTrigger>
                                  <TooltipContent className="w-[90vw] max-w-[600px] bg-[#0A1A2F] text-white border-0 shadow-lg">
                                    <p className="text-sm font-semibold text-[#00C2C2] mb-1">{t('scoreRationale')}</p>
                                    <p className="text-xs leading-relaxed">{row.score_rationale || t('noRationaleAvailable')}</p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                              {row.incumbent_sentiment && (
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <div className="inline-block cursor-help">
                                        <Badge className="bg-purple-50 text-purple-700 border border-purple-200 text-xs">
                                          {translateIncumbentSentiment(language, row.incumbent_sentiment)}
                                        </Badge>
                                      </div>
                                    </TooltipTrigger>
                                    <TooltipContent className="w-[90vw] max-w-[600px] bg-[#0A1A2F] text-white border-0 shadow-lg">
                                      <p className="text-sm font-semibold text-purple-300 mb-1">{t('incumbentSentiment')}</p>
                                      <p className="text-xs leading-relaxed">
                                        {row.incumbent_sentiment_rationale || t('noRationaleAvailable')}
                                      </p>
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              )}
                              {isPlataformaa && row.procurement_friendliness && (
                                <Badge className={`text-xs ${
                                  row.procurement_friendliness === 'Friendly' ? 'bg-green-100 text-green-700' :
                                  row.procurement_friendliness === 'Standard' ? 'bg-blue-100 text-blue-700' :
                                  row.procurement_friendliness === 'Bureaucratic' ? 'bg-yellow-100 text-yellow-700' :
                                  'bg-gray-100 text-gray-700'
                                }`}>
                                  {translateProcurementFriendliness(language, row.procurement_friendliness)}
                                </Badge>
                              )}
                              {isCalmedica && row.department && (
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <div className="inline-block cursor-help">
                                        <Badge className={`text-xs ${
                                          row.department_fit_score >= 90 ? 'bg-green-100 text-green-700' :
                                          row.department_fit_score >= 75 ? 'bg-blue-100 text-blue-700' :
                                          row.department_fit_score >= 60 ? 'bg-yellow-100 text-yellow-700' :
                                          'bg-gray-100 text-gray-700'
                                        }`}>
                                          {row.department} ({row.department_fit_score})
                                        </Badge>
                                      </div>
                                    </TooltipTrigger>
                                    <TooltipContent 
                                      side="bottom"
                                      className="w-[90vw] max-w-[400px] bg-[#0A1A2F] text-white border-0 shadow-lg p-3"
                                    >
                                      <p className="text-sm font-semibold text-[#00C2C2] mb-1">{row.department}</p>
                                      <p className="text-xs leading-relaxed">
                                        {row.department_fit_rationale || t('noRationaleAvailable')}
                                      </p>
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              )}
                              {showDecisionWindowColumn && (
                                hasDecisionWindowValue && row.decision_window_rationale ? (
                                  <TooltipProvider>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <div className="inline-block cursor-help">
                                          <Badge variant="outline" className="text-xs mr-2">
                                            {decisionWindow}
                                          </Badge>
                                        </div>
                                      </TooltipTrigger>
                                      <TooltipContent className="w-[90vw] max-w-[600px] bg-[#0A1A2F] text-white border-0 shadow-lg">
                                        <p className="text-sm font-semibold text-[#00C2C2] mb-1">{t('decisionWindowRationale')}</p>
                                        <p className="text-xs leading-relaxed">
                                          {row.decision_window_rationale}
                                        </p>
                                      </TooltipContent>
                                    </Tooltip>
                                  </TooltipProvider>
                                ) : hasDecisionWindowValue ? (
                                  <Badge variant="outline" className="text-xs mr-2">
                                    {decisionWindow}
                                  </Badge>
                                ) : null
                              )}
                              {isFaria && row.renewal_risk && (
                                <Badge className={`${getRenewalRiskColor(row.renewal_risk)} text-xs mr-2`}>
                                  {row.renewal_risk}
                                </Badge>
                              )}
                              <span className={`text-xs font-medium ${row.momentum_4w > 0 ? 'text-green-600' : row.momentum_4w < 0 ? 'text-red-600' : 'text-gray-500'}`}>
                                  {t('momentum')}: {row.momentum_4w > 0 ? `+${row.momentum_4w}` : row.momentum_4w || 0}
                                </span>
                            </div>
                          </div>
                          <div className="flex items-center gap-1 flex-shrink-0">
                            {showRemoveAction && (
                              <AccountOverflowMenu onRemove={() => setRemoveTarget(row)} />
                            )}
                            <ChevronRight className="w-4 h-4 text-gray-400" />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                );
              })}
            </div>
          )}
        </div>

        {!isLoading && filteredData.length === 0 && (
          <div className="text-center py-20">
            <Building2 className="w-12 h-12 mx-auto text-gray-300" />
            <h2 className="mt-4 text-xl font-semibold text-[#0A1A2F]">{t('noAccountsFound')}</h2>
            <p className="text-[#5C6B7A]">{t('adjustFilters')}</p>
          </div>
        )}
      </div>
    </div>
    <RemoveAccountDialog
      open={!!removeTarget}
      accountName={removeTarget?.account_name}
      busy={removeBusy}
      onOpenChange={(open) => !open && !removeBusy && setRemoveTarget(null)}
      onConfirm={confirmRemoveFromList}
    />
    </AccountPlaybookStatusProvider>
  );
}